import { useEffect, useState } from "react";
import api, { errorMessage } from "../../lib/api";
import { errorInfo } from "../../lib/collaborators";
import { LIMIT_TEXT } from "../../lib/mandates";
import useMandateTypes from "../../hooks/useMandateTypes";
import { StatePill } from "../CollaboratorBits";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { AlertCircle, Loader2, Pencil, Plus, Power } from "lucide-react";

const blank = { label: "", description: "", max_holders: "", expected: false };

// Référentiel des types de mandat. Seul « Gérant » existe au départ (la forme juridique de Jamora est une SARL) ; l'administrateur ajoute les autres
// types lui-même : rien n'est proposé d'office pour une autre forme juridique. La limite de titulaires est une donnée du type, pas une règle du CRM.
export default function MandateTypesPanel() {
  const { types, status, reload } = useMandateTypes();
  const [editing, setEditing] = useState(null); // "new" | type
  const [values, setValues] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    if (editing) setValues(editing === "new" ? blank : { label: editing.label, description: editing.description || "", max_holders: editing.max_holders ?? "", expected: !!editing.expected });
  }, [editing]);

  const run = async (request, success) => {
    setBusy(true);
    try {
      await request();
      toast.success(success);
      await reload();
      return true;
    } catch (error) {
      const info = errorInfo(error);
      toast.error(info.data?.msg || errorMessage(error));
      if (info.code === "version_conflict") await reload();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const limit = values.max_holders === "" ? null : Number(values.max_holders);
  const limitOk = limit === null || (Number.isInteger(limit) && limit >= 1 && limit <= 20);
  const save = async () => {
    const body = { label: values.label.trim(), description: values.description.trim() || null, max_holders: limit, expected: values.expected };
    const ok = editing === "new"
      ? await run(() => api.post("/collaborators/mandate-types", body), "Type de mandat ajouté")
      : await run(() => api.patch(`/collaborators/mandate-types/${editing.id}`, { expected_version: editing.version, ...body }), "Type de mandat mis à jour");
    if (ok) setEditing(null);
  };
  const toggle = async () => {
    const ok = await run(() => api.patch(`/collaborators/mandate-types/${toggling.id}`, { expected_version: toggling.version, active: !toggling.active }), toggling.active ? "Type désactivé" : "Type réactivé");
    if (ok) setToggling(null);
  };

  if (status === "loading") return <Skeleton className="h-32 w-full" />;
  if (status === "error") {
    return (
      <Card className="mx-auto max-w-xl space-y-3 p-8 text-center" data-testid="mandate-types-error">
        <AlertCircle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-600">Impossible de charger les types de mandat.</p>
        <Button variant="outline" onClick={reload}>Réessayer</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="mandate-types-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Un type désactivé reste affiché sur les fiches qui l'ont déjà mais n'est plus proposé.</p>
        <Button onClick={() => setEditing("new")} className="shrink-0" data-testid="btn-mandate-type-new"><Plus size={16} className="mr-1.5" /> Ajouter</Button>
      </div>
      <Card className="divide-y divide-slate-100 p-0">
        {types.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 p-4" data-testid={`mandate-type-${t.code}`}>
            <div className="min-w-0 space-y-0.5">
              <div className="flex flex-wrap items-center gap-2"><span className="break-words font-medium text-slate-900">{t.label}</span><StatePill active={t.active} />
                {t.expected && <span className="rounded-full border border-slate-200 px-2 py-0.5 text-2xs text-slate-600">Attendu dans l'entreprise</span>}</div>
              <div className="text-xs text-slate-500">{LIMIT_TEXT(t)}{t.description ? ` · ${t.description}` : ""}</div>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button variant="ghost" size="icon" aria-label={`Modifier ${t.label}`} onClick={() => setEditing(t)} data-testid={`btn-mandate-type-edit-${t.code}`}><Pencil size={15} /></Button>
              <Button variant="ghost" size="icon" aria-label={`${t.active ? "Désactiver" : "Réactiver"} ${t.label}`} onClick={() => setToggling(t)} data-testid={`btn-mandate-type-toggle-${t.code}`}><Power size={15} /></Button>
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="mandate-type-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Ajouter un type de mandat" : "Modifier le type de mandat"}</DialogTitle>
            <DialogDescription>Le code technique est fixé à la création ; le libellé, la limite et le reste restent modifiables.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label htmlFor="t-label">Libellé *</Label>
              <Input id="t-label" maxLength={120} className="text-base md:text-sm" value={values.label} onChange={(e) => setValues({ ...values, label: e.target.value })} data-testid="input-mandate-type-label" /></div>
            <div className="space-y-2"><Label htmlFor="t-desc">Description</Label>
              <Input id="t-desc" maxLength={500} className="text-base md:text-sm" value={values.description} onChange={(e) => setValues({ ...values, description: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="t-limit">Nombre maximal de titulaires en même temps</Label>
              <Input id="t-limit" type="number" min={1} max={20} placeholder="Aucune limite" className="text-base md:text-sm" value={values.max_holders} onChange={(e) => setValues({ ...values, max_holders: e.target.value })} data-testid="input-mandate-type-limit" />
              <p className="text-xs text-slate-500">Laissez vide si plusieurs titulaires sont possibles (plusieurs gérants d'une SARL, par exemple). Cette limite ne peut pas descendre sous le nombre de titulaires en poste ou à venir.</p></div>
            <label className="flex min-h-11 items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={values.expected} onChange={(e) => setValues({ ...values, expected: e.target.checked })} className="h-4 w-4" data-testid="check-mandate-type-expected" />
              Ce mandat est attendu dans l'entreprise (le contrôle de cohérence signalera son absence)
            </label>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Annuler</Button>
            <Button onClick={save} disabled={!values.label.trim() || !limitOk || busy} data-testid="btn-mandate-type-save">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toggling} onOpenChange={(v) => !v && setToggling(null)}>
        <DialogContent data-testid="mandate-type-toggle-dialog">
          <DialogHeader>
            <DialogTitle>{toggling?.active ? `Désactiver « ${toggling?.label} » ?` : `Réactiver « ${toggling?.label} » ?`}</DialogTitle>
            <DialogDescription>{toggling?.active ? "Les mandats déjà enregistrés de ce type restent en place et visibles ; il ne sera plus proposé pour de nouveaux mandats." : "Il pourra de nouveau être attribué."}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setToggling(null)} disabled={busy}>Annuler</Button>
            <Button onClick={toggle} disabled={busy} data-testid="btn-mandate-type-toggle-confirm">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} {toggling?.active ? "Désactiver" : "Réactiver"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
